import React, { useEffect, useRef, useState } from 'react';
import { AssetsConfig } from '@/services/AssetsConfig';
import { ProjectService } from '@/services/ProjectService';

const MoodboardCanvasViewer = ({ layers }) => {
      const canvasRef = useRef(null);
      const layerImagesRef = useRef({});
      const canvasWidth = 800;
      const canvasHeight = 600;
      const thumbnailSize = 200;
      const FALLBACK_IMAGE = 'https://gni-public-bucket.s3.us-east-1.amazonaws.com/assets/gni/notAvailible.jpg';

      const [customUrls, setCustomUrls] = useState({});

    const getLayerImageUrl = async item => {
        if (!item) return FALLBACK_IMAGE;

        // For custom layers with s3Key
        if (item.s3Key) {
            try {
                // First check if we already have the URL cached
                if (customUrls[item.s3Key]) {
                    return customUrls[item.s3Key];
                }

                // If not, download and create URL
                const response = await ProjectService.downloadFileFromS3(item.s3Key);
                const url = URL.createObjectURL(response);

                // Cache the URL
                setCustomUrls(prev => ({
                    ...prev,
                    [item.s3Key]: url,
                }));

                return url;
            } catch (error) {
                console.error('Error downloading custom image:', error);
                return FALLBACK_IMAGE;
            }
        }
        // For custom layers with s3Key
        if (item.s3Key) {
            try {
                // First check if we already have the URL cached
                if (customUrls[item.s3Key]) {
                    return customUrls[item.s3Key];
                }

                // If not, download and create URL
                const response = await ProjectService.downloadFileFromS3(item.s3Key);
                const url = URL.createObjectURL(response);

                // Cache the URL
                setCustomUrls(prev => ({
                    ...prev,
                    [item.s3Key]: url,
                }));

                return url;
            } catch (error) {
                console.error('Error downloading custom image:', error);
                return FALLBACK_IMAGE;
            }
        }

            // For regular items
            try {
                  const url = AssetsConfig.getImageUrl(item);
                  return url || FALLBACK_IMAGE;
            } catch (error) {
                  console.error('Error getting image URL:', error);
                  return FALLBACK_IMAGE;
            }
      };

      // Cleanup effect for custom URLs
      useEffect(() => {
            return () => {
                  // Cleanup any existing blob URLs when component unmounts
                  Object.values(customUrls).forEach(url => {
                        if (url.startsWith('blob:')) {
                              URL.revokeObjectURL(url);
                        }
                  });
            };
      }, [customUrls]);

      useEffect(() => {
            const loadImagesAndDraw = async () => {
                  if (!Array.isArray(layers)) {
                        console.error('Layers must be an array');
                        return;
                  }

                  const imageLoadPromises = layers.map(async layer => {
                        try {
                              const url = await getLayerImageUrl(layer.item);
                              return new Promise(resolve => {
                                    const img = new Image();
                                    img.src = url;
                                    img.onload = () => {
                                          layerImagesRef.current[layer.type] = img;
                                          resolve();
                                    };
                                    img.onerror = () => {
                                          console.error('Error loading image for layer:', layer);
                                          resolve();
                                    };
                              });
                        } catch (error) {
                              console.error('Error getting image URL for layer:', layer, error);
                              return Promise.resolve();
                        }
                  });

                  await Promise.all(imageLoadPromises);
                  drawCanvas();
            };

            loadImagesAndDraw();
      }, [layers, customUrls]);

      const drawCanvas = () => {
            const canvas = canvasRef.current;
            if (!canvas || !Array.isArray(layers)) return;

            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Sort layers by zIndex
            const sortedLayers = [...layers]
                  .filter(layer => layer.visible !== false)
                  .sort((a, b) => parseInt(a.zIndex) - parseInt(b.zIndex));

        sortedLayers.forEach(layer => {
            const img = layerImagesRef.current[layer.type];
            if (img) {
                const baseScale = Math.min(thumbnailSize / img.width, thumbnailSize / img.height);
                const currentScale = layer.scale ? parseFloat(layer.scale) : baseScale;
                const width = img.width * currentScale;
                const height = img.height * currentScale;
                const x = parseFloat(layer.position.x);
                const y = parseFloat(layer.position.y);

                ctx.drawImage(img, x, y, width, height);

                // Draw the label
                ctx.font = 'bold 13px arial'; 
        
                ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
                ctx.shadowBlur = 2;
                ctx.shadowOffsetX = 1;
                ctx.shadowOffsetY = 1;
                ctx.fillStyle = 'white';
                ctx.fillText(layer.type.split('_')[0], x + 8, y + height - 8);
            }
        });
    };

    return (
        <div className="w-full overflow-hidden">
            <div className="max-w-full">
                <div
                    className="relative bg-gray-50 rounded-lg mx-auto"
                    style={{
                        aspectRatio: '4/3',
                        width: '100%',
                        maxWidth: '800px',
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        width={canvasWidth}
                        height={canvasHeight}
                        className="w-full h-full border rounded-lg object-contain"
                    />
                </div>
            </div>
        </div>
    );
};

export default MoodboardCanvasViewer;
